import React, { useState } from 'react';
import { Stack, Typography, Divider, Grid } from '@mui/material';
import { Gallery } from 'components/Gallery';
import { CreativeUploader } from 'components/CreativeUploader';
import { CreativeUploadDialog, getCreativePreviewId } from 'components/CreativeUploadDialog';
import { UploadedCreativePreview } from 'components/UploadedCreativePreview';
import { NotUploadedFilesWarning } from 'components/NotUploadedFilesWarning';
import { InfoMessage } from 'components/InfoMessage';
import { WebsiteUrlRow } from '../Basic';
import { useCampaign, useCampaignForm } from 'hooks';
import { CampaignFields, CampaignStep, CampaignStepState, CreativeFile } from 'types';
import { getMatchingCreativeTypes, isNoPreviewByOriginalFileName } from 'utils';
import { MAX_CREATIVES_AMOUNT } from 'shared';

const errorMessages = {
  maxCreatives: `Max. ${MAX_CREATIVES_AMOUNT} Creatives pro Webseite`,
  wrongFormat:
    'Überprüfe nochmals die oben angegebenen Formate und folgende Vorgaben:\n' +
    'Bilder: Max. 150 KB, JPG, PNG, GIF (Mobile Rectangle: Max. 100 KB)\n' +
    'Video: Max. 100 MB, MP4, MOV, AVI\n' +
    `Max. ${MAX_CREATIVES_AMOUNT} Creatives`,
};

export const Creatives = () => {
  const {
    watch,
    updateWebsite,
    getFieldControls,
    formState: { errors },
    validateCreatives,
    updateStepsState,
    hasCreativesWarning,
  } = useCampaignForm();
  const { addCampaignCreativeMutation, deleteCampaignCreativeMutation, getCreativeTypesQuery } = useCampaign();

  const { websites } = watch();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isWrongFormat, setIsWrongFormat] = useState(false);
  const [isCreativeUploading, setIsCreativeUploading] = useState(false);
  const [invalidCreativesNumber, setInvalidCreativesNumber] = useState(0);
  const [validCreativesNumber, setValidCreativesNumber] = useState(0);

  const [isGalleryOpen, setIsGalleryOpen] = useState<number | null>(null);
  const [activeDialogIndex, setActiveDialogIndex] = useState(0);
  const [editCreativeIndex, setEditCreativeIndex] = useState(0);
  const [isCreativeReplace, setIsCreativeReplace] = useState(false);
  const [creativeFiles, setCreativeFiles] = useState<CreativeFile[]>([]);
  const [allUploadedFilesNumber, setAllUploadedFilesNumber] = useState(0);

  const { creativeTypes = [] } = getCreativeTypesQuery?.data ?? {};

  const saveCreatives = async (creatives: CreativeFile[]) => {
    setIsCreativeUploading(true);
    const activeWebsite = websites[activeDialogIndex];
    if (!activeWebsite) return;
    const savedCreatives = activeWebsite.creatives;

    const creativesToSave = isCreativeReplace
      ? savedCreatives.flatMap((creative, index) => (editCreativeIndex === index ? creatives : creative))
      : [...savedCreatives, ...creatives];

    updateWebsite(activeDialogIndex, { creatives: creativesToSave });

    for (const { file, creativeType } of creatives) {
      creativeType?.id &&
        (await addCampaignCreativeMutation.mutateAsync({
          file,
          websiteId: activeWebsite.dbId || 0,
          creativeTypeId: creativeType?.id,
        }));
    }

    setIsCreativeReplace(false);
    setIsDialogOpen(false);
    setIsCreativeUploading(false);
  };

  const deleteCreative = async (websiteIndex: number, creativeIndex: number) => {
    const website = websites[websiteIndex];
    if (!website) return;
    const creatives = website.creatives.filter((_, index) => index !== creativeIndex);
    const targetCreativeToDelete = website.creatives[creativeIndex]?.id;
    targetCreativeToDelete && deleteCampaignCreativeMutation.mutate(targetCreativeToDelete);
    updateWebsite(websiteIndex, { creatives });
    await validateCreatives(activeDialogIndex);
  };

  const uploadCreative = async (files: File[], index: number) => {
    updateStepsState(CampaignStepState.Complete, CampaignStep.Creatives);
    setIsWrongFormat(false);
    const creativeFiles = await Promise.all(
      files.map(async (file) => {
        const matchingTypes = await getMatchingCreativeTypes(file, creativeTypes);

        // basicly invalidate it
        if (matchingTypes.length === 0) {
          return [{ file, creativeType: null }];
        }

        return matchingTypes.map((creativeType) => ({ file, creativeType }));
      })
    );

    const flattendCreativeFiles = creativeFiles.flat();
    const validCreatives = flattendCreativeFiles.filter((creative) => creative.creativeType);

    const savedCreatives = watch()[CampaignFields.websites][index]?.creatives ?? [];
    const invalidCreatives = flattendCreativeFiles.filter((creative) => !creative.creativeType);
    setInvalidCreativesNumber(invalidCreatives.length);
    setValidCreativesNumber(validCreatives.length);
    const creativesToSave = isCreativeReplace
      ? savedCreatives.flatMap((creative, index) =>
          editCreativeIndex === index
            ? flattendCreativeFiles
            : { ...creative, creativeType: creative.creativeType ?? null }
        )
      : [...savedCreatives, ...creativeFiles];

    setActiveDialogIndex(index);
    setCreativeFiles(validCreatives);
    setAllUploadedFilesNumber(creativesToSave.length);

    if (creativesToSave.length > MAX_CREATIVES_AMOUNT || !validCreatives.length) {
      setIsWrongFormat(true);
      return;
    }

    openDialog();
  };

  const onReplaceCreative = async (files: File[], websiteIndex: number, creativeIndex: number) => {
    await uploadCreative(files, websiteIndex);
    setEditCreativeIndex(creativeIndex);
    setIsCreativeReplace(true);
  };

  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = async () => {
    await validateCreatives(activeDialogIndex);
    setInvalidCreativesNumber(0);
    setValidCreativesNumber(0);
    setIsDialogOpen(false);
  };

  const openGallery = (index: number, creativeIndex: number) => {
    setActiveDialogIndex(index);
    setEditCreativeIndex(creativeIndex);
    setIsGalleryOpen(index);
  };

  const getError = (index: number) => {
    return index === activeDialogIndex && allUploadedFilesNumber && !creativeFiles.length
      ? errorMessages.wrongFormat
      : errors.websites?.[index]?.creatives?.message;
  };

  const getCreativeControls = (index: number) =>
    getFieldControls(`${CampaignFields.websites}.${index}.creatives`, {
      validate: (value: CreativeFile[]) => {
        if (!value.length) {
          updateStepsState(CampaignStepState.Warning, CampaignStep.Creatives);
        }

        if (value.length > MAX_CREATIVES_AMOUNT) {
          return errorMessages.maxCreatives;
        }

        return true;
      },
      shouldSkipOnChange: true,
    });

  const warningMessage = invalidCreativesNumber ? (
    <NotUploadedFilesWarning
      totalFiles={validCreativesNumber + invalidCreativesNumber}
      removedFiles={invalidCreativesNumber}
    />
  ) : null;

  const getHasError = (index: number) => Boolean(getError(index)) || (isWrongFormat && index === activeDialogIndex);

  return (
    <Stack spacing={10} divider={<Divider flexItem orientation="horizontal" />}>
      {hasCreativesWarning && (
        <InfoMessage
          fullWidth
          title="Creatives hochladen"
          message={
            <Typography variant={'body2'}>
              Um die Bestellung abzuschliessen, lade bitte zuerst die fehlenden Creatives hoch.
            </Typography>
          }
          variant="warning"
        />
      )}
      {websites?.map(({ url, creatives }, websiteIndex) => (
        <Stack gap={6} key={url + '-' + websiteIndex} {...getCreativeControls(websiteIndex)}>
          <Stack>
            <WebsiteUrlRow url={url} component="h3" />
            <Typography variant="subtitle1">Max. 6 Creatives pro Webseite</Typography>
          </Stack>
          <Grid container spacing={6}>
            {!!creatives?.length &&
              creatives.map((creative, index) => (
                <Grid item sm={6} xs={12} key={creative.id ?? `${creative.file.name}-${index}`}>
                  <UploadedCreativePreview
                    id={getCreativePreviewId(index)}
                    isEdit
                    creative={creative}
                    onDelete={() => deleteCreative(websiteIndex, index)}
                    onOpenGallery={() => openGallery(websiteIndex, index)}
                    onReplace={(files) => onReplaceCreative(files, websiteIndex, index)}
                  />
                </Grid>
              ))}
            <Gallery
              url={url}
              isOpened={isGalleryOpen === websiteIndex}
              creatives={creatives.map((creative) => ({
                ...creative,
                isPreviewInvalid:
                  !!creative.originalFileName && isNoPreviewByOriginalFileName(creative.originalFileName),
              }))}
              selectedCreative={editCreativeIndex}
              onClose={() => setIsGalleryOpen(null)}
              onNext={() => setEditCreativeIndex((prev) => (prev === creatives.length - 1 ? 0 : prev + 1))}
              onPrev={() => setEditCreativeIndex((prev) => (prev === 0 ? creatives.length - 1 : prev - 1))}
            />
          </Grid>
          <Stack gap={2}>
            <CreativeUploader
              hasError={getHasError(websiteIndex)}
              onUpload={(files) => uploadCreative(files, websiteIndex)}
            />
            {getHasError(websiteIndex) && (
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }} fontSize={12} color="error.dark">
                {isWrongFormat ? errorMessages.wrongFormat : getError(websiteIndex)}
              </Typography>
            )}
          </Stack>
          <CreativeUploadDialog
            open={isDialogOpen}
            onClose={closeDialog}
            onSave={saveCreatives}
            creatives={creativeFiles}
            setCreatives={setCreativeFiles}
            warningMessage={warningMessage}
            websiteIndex={activeDialogIndex}
            isUploadDisabled={allUploadedFilesNumber > MAX_CREATIVES_AMOUNT}
            allUploadedFilesNumber={allUploadedFilesNumber}
            setAllUploadedFilesNumber={setAllUploadedFilesNumber}
            isUploading={isCreativeUploading}
          />
        </Stack>
      ))}
    </Stack>
  );
};
